import { useTranslation } from 'module/hooks/use-translation';
import { Button } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon } from 'module/ui/components/templates/argon';
import {
  faPlus,
  // faChevronDown,
  faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
// import { faBook } from '@fortawesome/pro-solid-svg-icons';
import { PageTemplateLite } from 'api/page-template/types';
import {
  useCreateTemporaryPeoplePageTemplateMutation,
  useDeletePeoplePageTemplateMutation,
  useUpdatePeoplePageTemplateOrderMutation,
  useUpdatePeoplePageTemplateStatusMutation,
} from 'queries/people-page-template';
import { useGetAllPeoplePageTemplatesQuery } from 'queries/people-page-template';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/route-paths';
// import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';
import { PeoplePageTemplateTable } from 'components/organisms/people-page-template/list-table/table';
import { PreConfigureTemplateModal } from 'components/organisms/people-page-template/pre-configure-template/modal';
import {
  PeoplePageTemplatesNoSearchMatchState,
  PeoplePageTemplatesNoTemplatesState,
} from './empty-state';
import { ContentWrapper, ListLoader } from '../../templates/styles';

// const StyledBookIcon = styled(FontAwesomeIcon)`
//   color: var(--brand-color);
// `;

export const PeoplePageTemplates = () => {
  const pageTitle = useTranslation('people_page_templates.title');
  const navigate = useNavigate();
  const { value: searchQuery, onChange: onSearchChange } =
    useSearchValueState();
  const searchInputRef = useRef<ExpandableSearchInputRef>(null);

  const { data: apiTemplates, isLoading } = useGetAllPeoplePageTemplatesQuery();
  const [templates, setTemplates] = React.useState<PageTemplateLite[]>([]);
  const deletePeoplePageTemplateMutation =
    useDeletePeoplePageTemplateMutation();
  const updatePeoplePageTemplateOrderMutation =
    useUpdatePeoplePageTemplateOrderMutation();
  const updatePeoplePageTemplateStatusMutation =
    useUpdatePeoplePageTemplateStatusMutation();

  React.useEffect(() => {
    if (apiTemplates) {
      setTemplates(apiTemplates);
    }
  }, [apiTemplates]);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery) {
      return templates;
    }

    return templates.filter((template) =>
      template.templateName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [templates, searchQuery]);

  const handleDelete = async (id: string) => {
    try {
      await deletePeoplePageTemplateMutation.mutateAsync(Number(id));
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
    // setTemplates(
    //   templates.filter((template) => String(template.templateId) !== id),
    // );
  };

  const handleReorder = async (
    newTemplates: PageTemplateLite[],
    movedTemplateId: number,
    newIndex: number,
  ) => {
    try {
      setTemplates(newTemplates);
      await updatePeoplePageTemplateOrderMutation.mutateAsync({
        templateId: movedTemplateId,
        order: newIndex,
      });
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleStatusChange = async (id: string) => {
    try {
      setTemplates(
        templates.map((template) =>
          String(template.templateId) === id
            ? { ...template, enabled: !template.enabled }
            : template,
        ),
      );
      await updatePeoplePageTemplateStatusMutation.mutateAsync({
        templateId: Number(id),
        status: !templates.find(
          (template) => template.templateId === Number(id),
        )?.enabled,
      });
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };
  const { mutateAsync: createTemporaryPeoplePageTemplate } =
    useCreateTemporaryPeoplePageTemplateMutation();
  const [
    preconfigureTemplateModalVisible,
    setPreconfigureTemplateModalVisible,
  ] = useState(false);
  const showPreconfigureTemplateModal = useCallback(() => {
    setPreconfigureTemplateModalVisible(true);
  }, []);
  const closePreconfigureTemplateModal = useCallback(() => {
    setPreconfigureTemplateModalVisible(false);
  }, []);
  const handleDone = useCallback(
    async (payload: { templateName: string }) => {
      closePreconfigureTemplateModal();
      createTemporaryPeoplePageTemplate(payload);
      navigate(ROUTE_PATHS.NEW);
    },
    [
      closePreconfigureTemplateModal,
      createTemporaryPeoplePageTemplate,
      navigate,
    ],
  );

  return (
    <>
      <Argon
        documentTitle={pageTitle}
        title={
          <HeaderWithIconTitle
            icon={<FontAwesomeIcon icon={faUserGroup} size={20} />}
            title={pageTitle}
          />
        }
        description={
          <FormattedMessage id="people_page_templates.description" />
        }
        actions={{
          secondary: (
            <FlexWrapperWithShortGap>
              <ExpandableSearchInput
                ref={searchInputRef}
                direction="right"
                value={searchQuery}
                onChange={onSearchChange}
                searchButtonProps={{
                  type: 'text',
                }}
                minWidth="360px"
              />
              {/* <Button>
                <FlexWrapperWithShortGap>
                  <StyledBookIcon icon={faBook} />
                  <FontAwesomeIcon icon={faChevronDown} />
                </FlexWrapperWithShortGap>
              </Button> */}

              <Button
                icon={<FontAwesomeIcon icon={faPlus} size={12} />}
                type="primary"
                onClick={showPreconfigureTemplateModal}
              >
                <FormattedMessage id="new_template" />
              </Button>
            </FlexWrapperWithShortGap>
          ),
        }}
        content={
          isLoading ? (
            <ListLoader />
          ) : (
            <ContentWrapper>
              {(apiTemplates?.length ?? 0) === 0 ? (
                <PeoplePageTemplatesNoTemplatesState
                  onPrimaryAction={showPreconfigureTemplateModal}
                />
              ) : filteredTemplates.length === 0 ? (
                <PeoplePageTemplatesNoSearchMatchState
                  onPrimaryAction={() =>
                    searchInputRef.current?.closeSearchInput()
                  }
                />
              ) : (
                <PeoplePageTemplateTable
                  templates={filteredTemplates}
                  onStatusChange={handleStatusChange}
                  onEdit={(id: string) => {
                    navigate(`${id}`);
                  }}
                  onDelete={handleDelete}
                  onReorder={handleReorder}
                  loading={isLoading}
                />
              )}
            </ContentWrapper>
          )
        }
      />
      <PreConfigureTemplateModal
        open={preconfigureTemplateModalVisible}
        onClose={closePreconfigureTemplateModal}
        onDone={handleDone}
      />
    </>
  );
};
